import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import SEO from '../components/SEO';

const ForgotPassword = () => {
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await authAPI.forgotPassword(data.email);
      if (response.data.success) {
        setEmailSent(true);
        toast.success('Password reset email sent successfully!');
      } else {
        toast.error(response.data.message || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-background py-12 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="Forgot Password" 
        description="Reset your TripTrot account password. Enter your email to receive password reset instructions"
        keywords={['forgot password', 'password reset', 'account recovery', 'trip trot password']}
        canonical="/forgot-password"
        noindex={true}
      />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Mail className="mx-auto h-12 w-12 text-theme-primary" />
          <h2 className="mt-6 text-3xl font-extrabold text-theme-primary">
            Forgot Password
          </h2>
          <p className="mt-2 text-sm text-theme-secondary">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {!emailSent ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-theme-primary">
                Email address
              </label>
              <div className="mt-1">
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  autoComplete="email"
                  className="appearance-none block w-full px-3 py-2 border border-theme-light rounded-md placeholder-theme-tertiary focus:outline-none focus:ring-theme-primary focus:border-theme-primary sm:text-sm"
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-theme-inverse bg-theme-primary hover:bg-theme-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-theme-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex items-center text-sm text-theme-secondary hover:text-theme-primary"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to login
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-8 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-theme-success" />
            <h3 className="mt-4 text-lg font-medium text-theme-primary">
              Reset Email Sent
            </h3>
            <p className="mt-2 text-sm text-theme-secondary">
              We've sent a password reset link to your email address. Please check your inbox and follow the instructions.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex items-center text-sm text-theme-secondary hover:text-theme-primary"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to login
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
